import { CheckCircle2, Loader2, MinusCircle, UserMinus, XCircle } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAdminUsersQuery } from "@/hooks/useAdminUsers";
import {
  useAddMailboxMember,
  useCreateMailbox,
  useRemoveMailboxMember,
  useTestMailboxConnection,
  useUpdateMailbox,
} from "@/hooks/useMailboxActions";
import { useMailboxesQuery } from "@/hooks/useMailboxes";
import type { Mailbox, TestConnectionResult } from "@/lib/mailboxesApi";

type ConnectionStatus =
  | { state: "idle" }
  | { state: "testing" }
  | { state: "result"; result: TestConnectionResult };

export function MailboxesPage() {
  const mailboxesQuery = useMailboxesQuery();
  const usersQuery = useAdminUsersQuery();

  const createMailbox = useCreateMailbox();
  const updateMailbox = useUpdateMailbox();
  const testConnection = useTestMailboxConnection();
  const addMember = useAddMailboxMember();
  const removeMember = useRemoveMailboxMember();

  // Create form state
  const [newLabel, setNewLabel] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newCalendar, setNewCalendar] = useState("");

  // Edit dialog state
  const [editMailbox, setEditMailbox] = useState<Mailbox | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editCalendar, setEditCalendar] = useState("");

  // Add member dialog state
  const [addMemberMailbox, setAddMemberMailbox] = useState<Mailbox | null>(null);
  const [addMemberUserId, setAddMemberUserId] = useState("");

  // Per-mailbox connection status map
  const [connStatus, setConnStatus] = useState<Record<string, ConnectionStatus>>({});

  const mailboxes = mailboxesQuery.data?.items ?? [];
  const allUsers = (usersQuery.data?.users as Array<{ id: string; email: string; name: string }>) ?? [];

  function handleCreate() {
    createMailbox.mutate(
      { label: newLabel, emailAddress: newEmail, calendarName: newCalendar },
      {
        onSuccess: () => {
          setNewLabel("");
          setNewEmail("");
          setNewCalendar("");
        },
      },
    );
  }

  function handleEdit() {
    if (!editMailbox) return;
    updateMailbox.mutate(
      { id: editMailbox.id, input: { label: editLabel, calendarName: editCalendar } },
      { onSuccess: () => setEditMailbox(null) },
    );
  }

  function handleToggleActive(mb: Mailbox) {
    updateMailbox.mutate({ id: mb.id, input: { isActive: !mb.isActive } });
  }

  function handleToggleMarkAsRead(mb: Mailbox) {
    updateMailbox.mutate({ id: mb.id, input: { markAsReadEnabled: !mb.markAsReadEnabled } });
  }

  function handleToggleCalendarSync(mb: Mailbox) {
    updateMailbox.mutate({ id: mb.id, input: { calendarSyncEnabled: !mb.calendarSyncEnabled } });
  }

  function handleBackfillDaysChange(mb: Mailbox, days: number) {
    if (days < 1 || days > 90 || !Number.isInteger(days)) return;
    updateMailbox.mutate({ id: mb.id, input: { backfillDays: days } });
  }

  function handleTestConnection(mb: Mailbox) {
    setConnStatus((prev) => ({ ...prev, [mb.id]: { state: "testing" } }));
    testConnection.mutate(mb.id, {
      onSuccess: (result) => {
        setConnStatus((prev) => ({ ...prev, [mb.id]: { state: "result", result } }));
      },
      onError: () => {
        setConnStatus((prev) => ({
          ...prev,
          [mb.id]: {
            state: "result",
            result: {
              email: { ok: false, error: "Errore imprevisto" },
              calendar: { ok: false, error: "Errore imprevisto" },
            },
          },
        }));
      },
    });
  }

  function handleAddMember() {
    if (!addMemberMailbox || !addMemberUserId) return;
    addMember.mutate(
      { mailboxId: addMemberMailbox.id, userId: addMemberUserId },
      { onSuccess: () => setAddMemberMailbox(null) },
    );
  }

  function handleRemoveMember(mailboxId: string, userId: string) {
    removeMember.mutate({ mailboxId, userId });
  }

  function renderConnectionBadge(mbId: string) {
    const status = connStatus[mbId];
    if (!status || status.state === "idle") {
      return (
        <Badge variant="outline" className="gap-1 text-muted-foreground">
          <MinusCircle className="h-3 w-3" />
          Non testata
        </Badge>
      );
    }
    if (status.state === "testing") {
      return (
        <Badge variant="outline" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Test in corso...
        </Badge>
      );
    }
    const { result } = status;
    const allOk = result.email.ok && result.calendar.ok;
    if (allOk) {
      return (
        <Badge variant="secondary" className="gap-1 text-green-700 dark:text-green-400">
          <CheckCircle2 className="h-3 w-3" />
          OK
        </Badge>
      );
    }
    const parts = [];
    if (!result.email.ok) parts.push("email");
    if (!result.calendar.ok) parts.push("cal");
    return (
      <Badge variant="red" className="gap-1">
        <XCircle className="h-3 w-3" />
        {parts.join(", ")} KO
      </Badge>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">Mailbox</h1>
        <p className="text-sm text-muted-foreground">
          Gestione delle caselle email monitorate
        </p>
      </div>

      {/* Create mailbox card */}
      <Card>
        <CardHeader>
          <CardTitle>Nuova mailbox</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="mb-label">Label</Label>
              <Input
                id="mb-label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Corriere del Ticino"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mb-email">Email</Label>
              <Input
                id="mb-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="eventi@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mb-calendar">Nome calendario</Label>
              <Input
                id="mb-calendar"
                value={newCalendar}
                onChange={(e) => setNewCalendar(e.target.value)}
                placeholder="Calendar"
              />
            </div>
          </div>
          <Button
            className="mt-4"
            onClick={handleCreate}
            disabled={createMailbox.isPending || !newLabel || !newEmail || !newCalendar}
          >
            {createMailbox.isPending ? "Creazione..." : "Crea mailbox"}
          </Button>
        </CardContent>
      </Card>

      {/* Mailboxes table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista mailbox</CardTitle>
          <CardDescription>
            {mailboxesQuery.isLoading ? "Caricamento..." : `${mailboxes.length} mailbox`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Label</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Calendario</TableHead>
                <TableHead>Utenti</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Segna lette</TableHead>
                <TableHead>Backfill (gg)</TableHead>
                <TableHead>Sync calendario</TableHead>
                <TableHead>Connessione</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mailboxes.map((mb) => (
                <TableRow key={mb.id}>
                  <TableCell className="font-medium">{mb.label}</TableCell>
                  <TableCell className="font-mono text-sm">{mb.emailAddress}</TableCell>
                  <TableCell className="text-sm">{mb.calendarName}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {mb.users.length === 0 ? (
                        <span className="text-xs text-muted-foreground">nessuno</span>
                      ) : (
                        mb.users.map((u) => (
                          <Badge key={u.id} variant="outline" className="gap-1 pr-1">
                            {u.email}
                            <button
                              type="button"
                              className="ml-0.5 rounded-sm opacity-60 hover:opacity-100"
                              onClick={() => handleRemoveMember(mb.id, u.id)}
                              title={`Rimuovi ${u.email}`}
                            >
                              <UserMinus className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {mb.isActive ? (
                      <Badge variant="secondary">Attiva</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        Disattiva
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={mb.markAsReadEnabled}
                      onCheckedChange={() => handleToggleMarkAsRead(mb)}
                      label="Segna email come lette"
                      disabled={updateMailbox.isPending}
                    />
                  </TableCell>
                  <TableCell>
                    <input
                      type="number"
                      min={1}
                      max={90}
                      defaultValue={mb.backfillDays}
                      disabled={mb.markAsReadEnabled || updateMailbox.isPending}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val !== mb.backfillDays) {
                          handleBackfillDaysChange(mb, val);
                        }
                      }}
                      className="w-16 rounded border bg-background px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                      title={mb.markAsReadEnabled ? "Non applicabile: le email vengono segnate come lette" : "Giorni indietro al primo ciclo (watermark)"}
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={mb.calendarSyncEnabled}
                      onCheckedChange={() => handleToggleCalendarSync(mb)}
                      label="Sincronizza calendario Outlook"
                      disabled={updateMailbox.isPending}
                    />
                  </TableCell>
                  <TableCell>{renderConnectionBadge(mb.id)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTestConnection(mb)}
                        disabled={connStatus[mb.id]?.state === "testing"}
                      >
                        Testa
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditMailbox(mb);
                          setEditLabel(mb.label);
                          setEditCalendar(mb.calendarName);
                        }}
                      >
                        Modifica
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(mb)}
                      >
                        {mb.isActive ? "Disattiva" : "Attiva"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setAddMemberMailbox(mb);
                          setAddMemberUserId("");
                        }}
                      >
                        + Membro
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={editMailbox !== null} onOpenChange={(open) => { if (!open) setEditMailbox(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica mailbox — {editMailbox?.emailAddress}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-label">Label</Label>
              <Input
                id="edit-label"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-calendar">Nome calendario</Label>
              <Input
                id="edit-calendar"
                value={editCalendar}
                onChange={(e) => setEditCalendar(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMailbox(null)}>
              Annulla
            </Button>
            <Button
              onClick={handleEdit}
              disabled={updateMailbox.isPending || !editLabel || !editCalendar}
            >
              {updateMailbox.isPending ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add member dialog */}
      <Dialog open={addMemberMailbox !== null} onOpenChange={(open) => { if (!open) setAddMemberMailbox(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi membro — {addMemberMailbox?.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="add-member-user">Utente</Label>
            <select
              id="add-member-user"
              value={addMemberUserId}
              onChange={(e) => setAddMemberUserId(e.target.value)}
              className="w-full rounded border bg-background px-3 py-2 text-sm"
            >
              <option value="">Seleziona un utente...</option>
              {allUsers
                .filter((u) => !addMemberMailbox?.users.some((m) => m.id === u.id))
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.email} ({u.name})
                  </option>
                ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMemberMailbox(null)}>
              Annulla
            </Button>
            <Button
              onClick={handleAddMember}
              disabled={addMember.isPending || !addMemberUserId}
            >
              {addMember.isPending ? "Aggiunta..." : "Aggiungi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
